import { SmallTitle } from '@/components/investors/shared';
import { SeoProps } from '@/components/shared/SEO';
import { InvestorsPageTemplate } from '@/pageTemplate/InvestorsPageTemplage';
import { Box, Flex, font } from '@lemonade-hq/cdk';
import { EnvelopeIcon, MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import styled from 'styled-components';

const data = [
  {
    question: 'On what stock exchange is Lemonade traded and under what symbol?',
    answer: 'Lemonade stock is traded on The New York Stock Exchange under the ticker symbol LMND.',
  },

  {
    question: "Where is Lemonade's corporate headquarters?",
    answer: 'Lemonade’s corporate headquarters is at 5 Crosby Street, 3rd Floor, New York, New York 10013.',
  },

  {
    question: 'Where is Lemonade incorporated?',
    answer: 'Lemonade is incorporated in Delaware.',
  },

  {
    question: 'What is Lemonade’s fiscal year?',
    answer: 'Lemonade’s fiscal year is based on the calendar year. The last day of the fiscal year is December 31.',
  },

  {
    question: 'How can I purchase Lemonade’s shares? Does Lemonade have a direct stock purchase plan?',
    answer:
      'Interested investors should consult a stockbroker. Lemonade does not offer a direct stock purchase plan at this time.',
  },

  {
    question: 'What is Lemonade’s CUSIP?',
    answer: 'The CUSIP number for Lemonade common stock is 52567D 107.',
  },

  {
    question: 'Does Lemonade pay dividends?',
    answer: 'Lemonade has not declared or paid cash dividends on its common stock to date.',
  },

  {
    question: 'Who is Lemonade’s transfer agent?',
    answer: 'Fidelity Stock Transfer℠ (FST) is our transfer agent and registrar',
  },

  {
    question: 'Whom do I contact with questions about my stock?',
    answer:
      'You may contact FST with any questions about your share ownership through any of the methods listed here:<br><br><strong>Phone:</strong> (Toll-Free) (833) 500-1036<br>Representatives are available to assist you by phone from 8:00 a.m. to 8:00 p.m. EST, Monday through Friday.<br><br><strong>Website:</strong> If you have an existing account with Fidelity, you can log in to view your account holdings as follows:<br>Log in to <a href="https://www.fidelity.com">Fidelity</a> or <a href="https://nb.fidelity.com/public/nb/default/home">NetBenefits Login Page – Fidelity</a><br><br><strong>Email:</strong> <a href="mailto:FSTOperations@fmr.com">FSTOperations@fmr.com</a><br><br><strong>Mail:</strong><br>Fidelity Investments<br>Attn: Fidelity Stock Transfer Solutions<br>PO Box 770017<br>Cincinnati, OH 45277<br><br><strong>Overnight Mail:</strong><br>Fidelity Investments<br>Attn: Fidelity Stock Transfer Solutions<br>100 Crosby Parkway<br>Mail Zone KY16<br>Covington, KY 41015',
  },

  {
    question: 'Who is Lemonade’s legal counsel?',
    answer: 'Latham & Watkins LLP',
  },

  {
    question: 'Who is Lemonade’s Independent Registered Public Accounting Firm?',
    answer: 'Lemonade’s independent auditors are Ernst & Young LLP.',
  },

  {
    question: 'How many employees does Lemonade have?',
    answer: 'As of May 2025, Lemonade had approximately 1,260 employees',
  },

  {
    question:
      'Where can I get the latest corporate news releases, financial reports and SEC Filings (including Forms 10-K and 10-Q)?',
    answer:
      'You can access Lemonade news releases within the <a href="/investor/news">News</a> section and financial reports (including SEC filings) within the <a href="/investor/filings">FIlings</a> section.',
  },

  {
    question: 'Who makes up the Lemonade Executive Management team and Board of Directors?',
    answer: `Biographies of Lemonade’s management team and the board can be found in the <a href="/investor/governance">Governance</a> section.`,
  },

  {
    question: 'Whom should I contact regarding investor inquiries?',
    answer: 'You can reach Lemonade Investor Relations at ir@lemonade.com.',
  },
];

const Answer = styled.p`
  font-size: 1.6rem;
  line-height: 145%;

  text-wrap: pretty;
`;

const Title = styled.h3`
  ${font('main', { fontSize: '1.6rem', lineHeight: '2.4rem', fontWeight: 700 })};
  text-wrap: pretty;
`;

const Content = styled.p`
  font-size: 1.6rem;
  line-height: 2.4rem;
  text-wrap: balance;
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ContactContentWrapper = styled(Flex)`
  gap: 10px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    gap: 80px
  `}
`;

const FaqItem = styled(Box)`
  padding-bottom: 20px;
  border-bottom: ${props => `1px solid ${props.theme.colors.glass}`};
`;

const ContactUs = () => {
  return (
    <Flex flexDirection="column" gap="16px" maxWidth="600px" mt="32px">
      <SmallTitle>Contact Us</SmallTitle>

      <ContactContentWrapper flexDirection={['column', 'row']} alignItems="flex-start">
        <ContactLink href="mailto:ir@lemonade.com">
          <EnvelopeIcon colorOverride="#ff0083" />
          ir@lemonade.com
        </ContactLink>

        <Flex width="100%" flexDirection="column" gap="3px">
          <SmallTitle>Lemonade Investors Relations</SmallTitle>
          <Content>
            Lemonade Insurance, 5 Crosby St. 3rd floor, <br />
            New York, NY 10013
          </Content>
        </Flex>
      </ContactContentWrapper>
    </Flex>
  );
};

const MAX_WIDTH = 700;

const seo = {
  title: 'Lemonade Inc. - Investor FAQs',
  description:
    'Get answers to frequently asked questions about Lemonade’s financials, stock, company strategy, and investor-related inquiries.',
} as SeoProps;

export default function FaqPage() {
  return (
    <InvestorsPageTemplate seo={seo}>
      <Flex flexDirection="column" width="100%" maxWidth={MAX_WIDTH} gap="20px" px={[20, 20, 0]}>
        {data.map(({ question, answer }) => (
          <FaqItem key={question}>
            <Title style={{ userSelect: 'none', marginBottom: '0px' }}>{question}</Title>
            <Answer>{parse(answer)}</Answer>
          </FaqItem>
        ))}
        <ContactUs />
      </Flex>
    </InvestorsPageTemplate>
  );
}
